import { AuthService } from "./authservice";
import { authFetch } from "./authFetch";
import { throwFetchApiError } from "../utils/apiErrorMessage";

// REACT_APP_API_CONNECTION already includes /api/v1/ at the end (e.g. https://api.beartrackapp.com/api/v1/).
// Strip that suffix so we can build full paths like /api/v1/portal/... without duplication.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(/\/api\/v1\/?$/, "");
const SSE_RECONNECT_DELAY = 3000;
// Techo del backoff exponencial de reconexión del stream de helpdesk: 5 min.
// Con ~63 contenedores y varios agentes con el inbox abierto, reintentar cada
// 3s contra un backend caído multiplicaba la carga justo cuando peor estaba.
const SSE_MAX_RECONNECT_DELAY = 300000;

// ─── Headers ─────────────────────────────────────────────────────────────────

// El Authorization lo pone authFetch (token vigente + un reintento ante 401).
function buildAuthHeaders(extra = {}) {
  return {
    "x-app": "web",
    application: "web",
    ...extra,
  };
}

/**
 * Devuelve un accessToken FRESCO para (re)abrir un stream. Si el actual está
 * vencido (o a <5min de vencer), lo refresca con el refreshToken ANTES de
 * conectar y actualiza localStorage. Sin esto, una reconexión reusaba el token
 * rancio y entraba en un bucle 401 silencioso: el EventSource no expone el
 * status HTTP, así que un 401 no se puede detectar en onerror.
 *
 * Devuelve null si no hay forma de obtener uno válido (sesión expirada / refresh
 * falló). En ese caso el llamador debe DETENER la reconexión — no seguir
 * intentando en vano; el guard de rutas de la app maneja el redirect a login.
 */
async function getFreshAccessToken() {
  return AuthService.getValidAccessToken();
}

// ─── Chat con IA (streaming NDJSON) ─────────────────────────────────────────

/**
 * @param {object} opts
 * @param {Array}  opts.messages      - Historial completo de la conversacion
 * @param {object} opts.user          - Usuario autenticado (solo para compat, ya no se usan sus campos directamente)
 * @param {function} opts.onChunk     - Fragmento de texto de la IA
 * @param {function} opts.onStatus    - Chunk de estado (looking_up, searching)
 * @param {function} opts.onTransfer  - Handoff detectado: { conversation_id, message }
 * @param {function} opts.onDone      - Stream completo (sin handoff)
 * @param {function} opts.onError     - Error de red o parsing: (message, err).
 *   El 2º argumento es el Error original con `.status` (HTTP) cuando lo hubo —
 *   sin él, el widget no puede distinguir "el asistente no está desplegado"
 *   (404/502/503, red) de un error real del turno.
 * @param {AbortSignal} opts.signal
 */
export async function postChat({ messages, user, onChunk, onStatus, onTransfer, onDone, onError, signal }) {
  try {
    const response = await authFetch(`${BFF_BASE_URL}/api/v1/portal/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...buildAuthHeaders(),
      },
      // El modelo/proveedor lo decide el BACKEND: mandarlo desde acá fijaba
      // llama3.2 en todos los tenants e ignoraba el agente configurado.
      body: JSON.stringify({ messages }),
      signal,
    });

    if (!response.ok) await throwFetchApiError(response);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let fullText = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        const jsonStr = trimmed.startsWith("data: ") ? trimmed.slice(6) : trimmed;

        try {
          const data = JSON.parse(jsonStr);

          // Chunk de transferencia (handoff)
          if (data.status === "transferred") {
            onTransfer?.({ conversation_id: data.conversation_id, message: data.message });
            return;
          }

          // Chunks de estado intermedios
          if (data.status === "looking_up" || data.status === "searching") {
            onStatus?.(data);
            continue;
          }

          // Fragmento de texto de la IA
          const chunk = data.message?.content ?? data.choices?.[0]?.delta?.content ?? data.response ?? "";
          if (chunk) {
            fullText += chunk;
            onChunk?.(chunk);
          }

          if (data.done === true) {
            onDone?.(fullText);
            return;
          }
        } catch {
          // Linea no es JSON valido, ignorar
        }
      }
    }
    onDone?.(fullText);
  } catch (err) {
    if (err.name !== "AbortError") {
      onError?.(err.message || "No se pudo conectar con el asistente.", err);
    }
  }
}

// ─── Mensajes de usuario (post-handoff) ─────────────────────────────────────

export async function postMessage(conversationId, content, user) {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/portal/conversations/${conversationId}/messages`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
    body: JSON.stringify({ content }),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

// ─── Helpdesk — Routing por canal ────────────────────────────────────────────

// Conversaciones Portal viven en el servicio de IA (via helpdesk proxy).
// WhatsApp, Messenger e Instagram viven en MongoDB del microservice-conversations.
function convBasePath(channel) {
  return channel && channel !== "portal"
    ? `${BFF_BASE_URL}/api/v1/conversations`
    : `${BFF_BASE_URL}/api/v1/helpdesk/conversations`;
}

/**
 * ¿Un evento SSE del canal `channel` corresponde al filtro `filter` activo?
 *
 * Vive acá —y no en la vista— porque es la MISMA regla que decide si una
 * conversación nueva entra a la bandeja en vivo: si divergiera, el listado y el
 * SSE contarían cosas distintas. Es una función pura y por eso se puede probar
 * sin montar el inbox.
 *
 * - `whatsapp` agrupa oficial (whatsapp) + no oficial (whatsapp_unofficial),
 *   igual que el fetch del backend (ver ConversationsService).
 * - `internal` es un canal que NO comparte bandeja: sus conversaciones solo
 *   entran en su propia vista y nunca en "Todas" ni en el resto de canales.
 *
 * @param {string|null|undefined} channel Canal de la conversación/evento
 * @param {string|null|undefined} filter  Canal del filtro activo ("" = Todas)
 * @returns {boolean}
 */
export function channelMatchesFilter(channel, filter) {
  if (filter === "internal") return channel === "internal";
  if (channel === "internal") return false;
  if (!filter) return true; // "Todas" / sin filtro de canal
  if (!channel) return true; // evento sin canal: no lo descartamos
  if (filter === "whatsapp") {
    return channel === "whatsapp" || channel === "whatsapp_unofficial";
  }
  return channel === filter;
}

// ─── Helpdesk — Listado de conversaciones Portal (AI service) ────────────────

/**
 * @param {object} params - { status, company_id, assigned_to, limit, offset }
 * @param {object} user
 */
export async function getConversations(params = {}, user) {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.company_id) qs.set("company_id", params.company_id);
  if (params.assigned_to) qs.set("assigned_to", params.assigned_to);
  qs.set("limit", String(params.limit ?? 1000));
  qs.set("offset", String(params.offset ?? 0));

  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/conversations?${qs}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Error ${response.status}`);
  }
  return response.json(); // { conversations, count }
}

// ─── Helpdesk — Listado de conversaciones omnicanal (MongoDB) ────────────────

/**
 * @param {object} params - { channel, status, company_id, effective_commerce_id, assigned_to, include_all, folder_id, limit, offset }
 *   `folder_id`: filtra por carpeta del helpdesk ('none' = sin carpeta). Solo
 *   aplica a la fuente omnicanal — portal/IA no tienen folder_id.
 */
export async function getOmniConversations(params = {}) {
  const qs = new URLSearchParams();
  if (params.channel) qs.set("channel", params.channel);
  if (params.status) qs.set("status", params.status);
  if (params.company_id) qs.set("company_id", params.company_id);
  if (params.folder_id) qs.set("folder_id", params.folder_id);
  // Filtro por comercio efectivo de la conversación (el backend lo aplica sobre
  // effective_commerce_id, un índice directo). Sin esta línea el filtro de
  // "Comercio" del inbox se armaba en la UI pero NUNCA viajaba en la query.
  if (params.effective_commerce_id)
    qs.set("effective_commerce_id", params.effective_commerce_id);
  if (params.assigned_to) qs.set("assigned_to", params.assigned_to);
  // Filtro por etiqueta (una a la vez): el backend lo aplica sobre `labels`.
  if (params.label_id) qs.set("label_id", params.label_id);
  // Monitoreo IA BYO: 'active' (la IA está respondiendo) | 'escalated' |
  // 'handled' (la IA respondió o derivó en 30 días, resueltas incluidas).
  if (params.ai_state) qs.set("ai_state", params.ai_state);
  if (params.include_all) qs.set("include_all", "true");
  // Vista de UN número retirado sin reemplazo: sus conversaciones no están en
  // ninguna otra bandeja (el backend las excluye por defecto).
  if (params.retired_account_id) qs.set("retired_account_id", params.retired_account_id);
  qs.set("limit", String(params.limit ?? 1000));
  qs.set("offset", String(params.offset ?? 0));

  const response = await authFetch(`${BFF_BASE_URL}/api/v1/conversations?${qs}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Error ${response.status}`);
  }
  return response.json(); // { conversations, count }
}

// ─── Helpdesk — Detalle de conversacion ─────────────────────────────────────

export async function getConversation(id, user, channel) {
  const response = await authFetch(`${convBasePath(channel)}/${id}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

// ─── Helpdesk — Asignar conversacion ────────────────────────────────────────

/**
 * @param {string} id           - ID de la conversacion
 * @param {string|null} agentId - null = auto-asignacion al agente actual
 * @param {object} user
 * @param {string} [channel]    - Canal de la conversacion para routing correcto
 */
export async function assignConversation(id, agentId, user, channel) {
  const body = agentId ? { agent_id: agentId } : {};
  const response = await authFetch(`${convBasePath(channel)}/${id}/assign`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

// ─── Helpdesk — Resolver conversacion ───────────────────────────────────────

export async function resolveConversation(id, user, channel) {
  const response = await authFetch(`${convBasePath(channel)}/${id}/resolve`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

// ─── Helpdesk — Cerrar en bloque las abiertas de un número retirado ─────────
//
// Cierre general (no borra nada: los hilos quedan en "Resueltos" y en la
// búsqueda). El backend procesa por tandas para no pasar el timeout del BFF y
// devuelve { resolved, failed, remaining }: el llamador repite mientras
// `remaining > 0`. Requiere rol de supervisor o administrador (403 si no).
export async function resolveOpenOfRetiredAccount(accountId) {
  const response = await authFetch(
    `${BFF_BASE_URL}/api/v1/conversations/retired-accounts/${accountId}/resolve-open`,
    { method: "POST", headers: buildAuthHeaders() },
  );
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

// ─── Helpdesk — Tomar una atención atendida por IA (takeover) ───────────────
//
// Las atenciones del portal atendidas por IA se guardan como LOGS
// (`ai_chat_logs`), que NO son conversaciones: por eso intervenir con el id del
// log daba 404 "Conversation not found". Este endpoint del bridge promueve el
// log a una conversación real, la asigna al agente y deja al widget del cliente
// en modo humano (la IA deja de responderle). Devuelve la conversación creada,
// con la que el detalle reemplaza la vista del log.
//
// Ruta EXPLÍCITA (no convBasePath): el takeover vive en el servicio de IA bajo
// `ai-logs/`, no en `conversations`. throwFetchApiError preserva `err.status` para
// que el llamador distinga con isEndpointMissing() un 404/405 —"falta desplegar
// el bridge"— de un error real, y avise en vez de pintar un error rojo.
export async function takeoverAiLog(logId) {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/ai-logs/${logId}/takeover`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

// ─── Helpdesk — Renombrar conversación (grupos de WhatsApp) ─────────────────

/**
 * Cambia el nombre visible de una conversación. Su caso de uso son los grupos de
 * WhatsApp, que llegan identificados solo por el JID numérico del grupo
 * (p. ej. "120363382372162507"): sin un nombre puesto a mano no hay forma de
 * saber de qué grupo se trata.
 *
 * La ruta `PATCH /conversations/:id/name` YA existe en el backend. Un 404/405
 * SIN mensaje del backend (err.serverMessage null: el default de Nest para una
 * ruta ausente es "Cannot PATCH /…") significa que la ruta no está desplegada en
 * ese entorno; un 404 CON mensaje de Nest ('Conversacion no encontrada') sí es un
 * error real. Por eso usa throwFetchApiError (que preserva `err.status` y
 * `err.serverMessage`): el llamador distingue ambos casos con isEndpointMissing()
 * y muestra un aviso solo en el primero.
 *
 * @param {string} id      - ID de la conversación
 * @param {string} name    - Nuevo nombre (ya validado/trim por la UI)
 * @param {string} [channel] - Canal de la conversación para routing correcto
 * @returns {Promise<object>} Conversación actualizada (o { conversation })
 */
export async function renameConversation(id, name, channel) {
  const response = await authFetch(`${convBasePath(channel)}/${id}/name`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

// ─── Helpdesk — Enviar mensaje como agente ───────────────────────────────────

/**
 * @param {string} conversationId
 * @param {string} content        - Texto del mensaje (puede ser vacío si se adjunta archivo)
 * @param {object} user
 * @param {string} channel        - Canal de la conversacion
 * @param {File|null} [file]      - Archivo adjunto opcional
 * @param {string|null} [contentType] - Tipo explícito (p. ej. "voice_note" para
 *   que el backend lo envíe como nota de voz ptt en vez de audio genérico). Si se
 *   omite, el backend lo deriva del mime del archivo.
 */
export async function postHelpdeskMessage(
  conversationId,
  content,
  user,
  channel,
  file = null,
  contentType = null,
) {
  const url = `${convBasePath(channel)}/${conversationId}/messages`;

  if (file) {
    const formData = new FormData();
    if (content) formData.append("content", content);
    formData.append("file", file);
    if (contentType) formData.append("content_type", contentType);
    const response = await authFetch(url, {
      method: "POST",
      headers: buildAuthHeaders(),
      body: formData,
    });
    if (!response.ok) await throwFetchApiError(response);
    return response.json();
  }

  const response = await authFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
    body: JSON.stringify({ content }),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

// ─── Helpdesk — Logs de conversaciones atendidas por IA ─────────────────────

/**
 * @param {object} params - { company_id, limit, offset }
 */
export async function getAILogs(params = {}) {
  const qs = new URLSearchParams();
  if (params.company_id) qs.set("company_id", params.company_id);
  qs.set("limit", String(params.limit ?? 1000));
  qs.set("offset", String(params.offset ?? 0));
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/ai-logs?${qs}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Error ${response.status}`);
  return response.json(); // { logs, count }
}

/**
 * @param {string} id - ID del AI log
 */
export async function getAILog(id) {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/ai-logs/${id}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

// ─── Helpdesk — Errores tipados (degradación por endpoint no desplegado) ────

/**
 * true cuando el error viene de una ruta que el backend todavía no expone
 * (404 Not Found / 405 Method Not Allowed). Las vistas deben usarlo para mostrar
 * un aviso "pendiente de backend" en vez de un error rojo: hoy ninguno de los
 * endpoints de búsqueda / respuestas rápidas / read está desplegado.
 * @param {unknown} err - Error lanzado por cualquier función de este módulo
 * @returns {boolean}
 */
export function isEndpointMissing(err) {
  return err?.status === 404 || err?.status === 405;
}

// ─── Helpdesk — Búsqueda de mensajes ────────────────────────────────────────

/**
 * Busca mensajes por texto, opcionalmente acotado a una conversación o canal.
 * @param {object} params
 * @param {string} params.q                 - Texto a buscar
 * @param {string} [params.conversation_id] - Acota a una conversación
 * @param {string} [params.channel]         - Acota a un canal
 * @param {number} [params.limit=50]
 * @param {number} [params.offset=0]
 * @returns {Promise<{results: Array, count: number}>}
 */
export async function searchMessages({ q, conversation_id, channel, limit = 50, offset = 0 } = {}) {
  const qs = new URLSearchParams();
  qs.set("q", q ?? "");
  if (conversation_id) qs.set("conversation_id", conversation_id);
  if (channel) qs.set("channel", channel);
  qs.set("limit", String(limit));
  qs.set("offset", String(offset));

  const response = await authFetch(`${BFF_BASE_URL}/api/v1/conversations/search?${qs}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  const data = await response.json().catch(() => ({}));
  // Forma normalizada: la UI itera `results` sin revalidar el shape en cada render.
  //
  // El backend proyecta los campos del mensaje y los de su conversación PLANOS
  // (`id`, `contact_name`, `channel`, `external_id`); el contrato de la vista los
  // pide anidados (`message_id`, `conversation.*`). Se agregan los alias anidados
  // SIN quitar los planos, así la vista funciona con cualquiera de las dos formas
  // y el adaptador vive acá y no repartido por los componentes.
  const results = (data.results ?? []).map((r) => ({
    ...r,
    message_id: r.message_id ?? r.id,
    conversation: r.conversation ?? {
      contact_name: r.contact_name,
      channel: r.channel,
      external_id: r.external_id,
    },
  }));
  return { results, count: data.count ?? 0 };
}

// ─── Helpdesk — Ticket ──────────────────────────────────────────────────────

/**
 * Resuelve un ticket a su conversación, su caso y su estado. Es la vía para
 * encontrar una interacción pasada sin saber de quién era.
 * @param {number|string} number - Número de ticket
 * @returns {Promise<object|null>} null si el ticket no existe (404)
 */
export async function getTicket(number) {
  const response = await authFetch(
    `${BFF_BASE_URL}/api/v1/conversations/tickets/${encodeURIComponent(number)}`,
    { headers: buildAuthHeaders() },
  );
  // 404 = el ticket NO EXISTE. No es un fallo: se devuelve null para que la
  // vista diga "no existe el ticket #N" en vez de un error de red.
  if (response.status === 404) return null;
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

// ─── Helpdesk — Respuestas rápidas ──────────────────────────────────────────
//
// Van bajo el prefijo `helpdesk/` a propósito: el BFF solo registra
// controladores proxy para conversations / webhooks / portal / helpdesk /
// channel-integrations / ai-logs. `/api/v1/quick-replies` no lo proxea nadie
// (404 permanente). El microservicio registra el controller con doble prefijo
// (['quick-replies', 'helpdesk/quick-replies']), así que esta ruta llega sin
// reescritura de path.

/**
 * @returns {Promise<{quick_replies: Array}>}
 */
export async function getQuickReplies() {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/quick-replies`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  const data = await response.json().catch(() => ({}));
  return { quick_replies: data.quick_replies ?? [] };
}

/**
 * @param {object} data - { title, body, shortcut, category, channel, sort_order, active }
 * @returns {Promise<{quick_reply: object}>}
 */
export async function createQuickReply(data) {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/quick-replies`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

/**
 * @param {string} id
 * @param {object} data - Campos parciales de QuickReply a actualizar
 * @returns {Promise<{quick_reply: object}>}
 */
export async function updateQuickReply(id, data) {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/quick-replies/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json();
}

/**
 * @param {string} id
 * @returns {Promise<{ok: boolean}>}
 */
export async function deleteQuickReply(id) {
  const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/quick-replies/${id}`, {
    method: "DELETE",
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  // Un DELETE puede responder 204 sin body: parsear JSON ahí tira SyntaxError.
  if (response.status === 204) return { ok: true };
  return response.json();
}

// ─── Helpdesk — Marcar conversación como leída ──────────────────────────────

/**
 * Resetea `unread_count` de la conversación (ruta /api/v1/conversations/:id/read).
 *
 * Es no-op para Portal: `unread_count` solo existe en las conversaciones
 * omnicanal de Mongo — las de Portal viven en el servicio de IA, que no expone
 * /read bajo el proxy /helpdesk. Sin este corte, seleccionar una conversación de
 * Portal dispararía un 404 en cada apertura del detalle.
 * @param {string} id
 * @param {string} [channel]
 * @returns {Promise<{conversation: object|null}>}
 */
export async function markConversationRead(id, channel) {
  if (!channel || channel === "portal") return { conversation: null };

  const response = await authFetch(`${convBasePath(channel)}/${id}/read`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(),
    },
  });
  if (!response.ok) await throwFetchApiError(response);
  if (response.status === 204) return { conversation: null };
  return response.json();
}

// ─── SSE — Utilidad interna para leer bloques SSE ─────────────────────────

function parseSseBlocks(buffer) {
  const blocks = buffer.split("\n\n");
  const remainder = blocks.pop();
  const events = [];

  for (const block of blocks) {
    if (!block.trim() || block.startsWith(":")) continue;
    const eventMatch = block.match(/^event: (.+)$/m);
    const dataMatch = block.match(/^data: (.+)$/m);
    if (dataMatch) {
      try {
        events.push({
          type: eventMatch ? eventMatch[1].trim() : "message",
          data: JSON.parse(dataMatch[1].trim()),
        });
      } catch {
        // JSON invalido, ignorar
      }
    }
  }
  return { events, remainder };
}

// ─── SSE — Stream de conversacion especifica (usuario post-handoff) ──────────

/**
 * @param {string} conversationId
 * @param {object} user
 * @param {object} handlers - { onMessage, onAssigned, onResolved, onError }
 * @returns {function} cleanup - funcion para detener la conexion
 */
export function connectConversationStream(conversationId, user, handlers = {}) {
  let abortController = new AbortController();
  let stopped = false;

  async function connect() {
    if (stopped) return;
    try {
      const response = await authFetch(
        `${BFF_BASE_URL}/api/v1/portal/conversations/${conversationId}/stream`,
        {
          headers: buildAuthHeaders(),
          signal: abortController.signal,
        }
      );

      if (!response.ok) throw new Error(`SSE error ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remainder } = parseSseBlocks(buffer);
        buffer = remainder;

        for (const { type, data } of events) {
          if (type === "new_message") handlers.onMessage?.(data);
          else if (type === "assigned") handlers.onAssigned?.(data);
          else if (type === "resolved") handlers.onResolved?.(data);
          else if (type === "message_sent") handlers.onMessage?.(data);
        }
      }
    } catch (err) {
      if (err.name === "AbortError" || stopped) return;
      handlers.onError?.(err);
      setTimeout(connect, SSE_RECONNECT_DELAY);
    }
  }

  connect();

  return function cleanup() {
    stopped = true;
    abortController.abort();
  };
}

// ─── SSE — Stream global de helpdesk (agentes/supervisores) ─────────────────

/**
 * @param {object} user
 * @param {object} handlers - { onNewConversation, onNewMessage, onAssignmentChange, onStatusChange, onMessageStatus, onReconnect, onError }
 * @returns {function} cleanup
 */
export function connectHelpdeskStream(user, handlers = {}) {
  let abortController = new AbortController();
  let stopped = false;
  let openedOnce = false;
  // Backoff exponencial: arranca en el delay histórico, se duplica en cada
  // fallo CONSECUTIVO y se reinicia solo cuando llega el primer byte del cuerpo
  // (ver abajo). La secuencia es 3s → 6s → 12s → … → 300s, así que una caída
  // larga deja de martillar el backend sin dejar de reintentar nunca.
  let reconnectDelay = SSE_RECONNECT_DELAY;
  let reconnectTimer = null;

  function scheduleReconnect() {
    const delay = reconnectDelay;
    reconnectDelay = Math.min(reconnectDelay * 2, SSE_MAX_RECONNECT_DELAY);
    reconnectTimer = setTimeout(connect, delay);
  }

  async function connect() {
    if (stopped) return;
    const token = await getFreshAccessToken();
    if (stopped) return;
    if (!token) {
      // Sesión expirada / refresh falló: no reintentar en bucle. El guard de
      // rutas de la app se encarga del redirect a login.
      handlers.onError?.(new Error("Helpdesk SSE detenido: sin token válido"));
      return;
    }
    try {
      const response = await authFetch(`${BFF_BASE_URL}/api/v1/helpdesk/stream`, {
        headers: buildAuthHeaders(),
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`SSE error ${response.status}`);

      // (Re)conectado: reconciliar con la fuente de verdad (Mongo) para no
      // perder lo emitido durante el corte. Se omite la PRIMERA conexión (el
      // montaje del inbox ya hizo el fetch inicial).
      if (openedOnce) handlers.onReconnect?.();
      openedOnce = true;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let receivedData = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // La racha de fallos se da por terminada con el PRIMER byte del cuerpo,
        // NO con los headers: un 200 que cierra sin mandar nada (proxy, sesión
        // rechazada) no es una conexión sana, y devolver el delay al mínimo
        // dejaría reintentando cada 3s contra algo que no responde.
        if (!receivedData) {
          receivedData = true;
          reconnectDelay = SSE_RECONNECT_DELAY;
        }

        buffer += decoder.decode(value, { stream: true });
        const { events, remainder } = parseSseBlocks(buffer);
        buffer = remainder;

        for (const { type, data } of events) {
          if (type === "new_conversation") handlers.onNewConversation?.(data);
          else if (type === "new_message") handlers.onNewMessage?.(data);
          else if (type === "assignment_change") handlers.onAssignmentChange?.(data);
          else if (type === "status_change") handlers.onStatusChange?.(data);
          else if (type === "message_status") handlers.onMessageStatus?.(data);
        }
      }

      // El servidor cerró el stream (fin de cuerpo, sin error): para el inbox es
      // una desconexión igual que un fallo, y hasta acá dejaba el stream muerto
      // para siempre. Se reconecta conservando la racha (si venía recibiendo
      // datos, el delay ya se reinició arriba).
      if (!stopped) scheduleReconnect();
    } catch (err) {
      if (err.name === "AbortError" || stopped) return;
      handlers.onError?.(err);
      scheduleReconnect();
    }
  }

  connect();

  return function cleanup() {
    stopped = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    abortController.abort();
  };
}

// ─── SSE — Stream omnicanal (WhatsApp/Messenger/Instagram) ────────────────

/**
 * SSE stream for omnichannel conversations stored in microservice-conversations
 * (whatsapp, whatsapp_unofficial, messenger, instagram). Runs in parallel with
 * connectHelpdeskStream(); they cover disjoint sets of conversations.
 *
 * EventSource is used (not fetch streaming) because NestJS @Sse() returns
 * standard SSE format. Token is passed via query param since EventSource
 * cannot set Authorization headers.
 *
 * @param {object} user
 * @param {object} handlers - { onNewConversation, onNewMessage, onAssignmentChange, onStatusChange, onMessageStatus, onNotificationsInbound, onReconnect, onError }
 *   `onNotificationsInbound({conversation, message})`: respuesta de un contacto
 *   en un número dedicado a notificaciones (no llega como new_conversation ni
 *   new_message; quien no incluye esos números lo ignora).
 * @returns {function} cleanup
 */
export function connectOmniHelpdeskStream(user, handlers = {}) {
  let stopped = false;
  let es = null;
  let reconnectTimer = null;
  let openedOnce = false;
  // Mismo backoff exponencial que connectHelpdeskStream (3s → 6s → … → 300s),
  // por la misma razón: varios inbox abiertos reintentando cada 3s contra un
  // backend caído multiplicaban la carga justo cuando peor estaba.
  let reconnectDelay = SSE_RECONNECT_DELAY;
  let receivedData = false;

  function scheduleReconnect() {
    const delay = reconnectDelay;
    reconnectDelay = Math.min(reconnectDelay * 2, SSE_MAX_RECONNECT_DELAY);
    reconnectTimer = setTimeout(open, delay);
  }

  async function open() {
    if (stopped) return;
    const token = await getFreshAccessToken();
    if (stopped) return;
    if (!token) {
      // Sin token válido: cortar el bucle de reconexión (fin del 401 infinito
      // silencioso). El guard de rutas de la app maneja el redirect a login.
      handlers.onError?.(new Error("Omni SSE detenido: sin token válido"));
      return;
    }
    const url = `${BFF_BASE_URL}/api/v1/helpdesk/omni-stream?token=${encodeURIComponent(token)}`;
    es = new EventSource(url);
    // Cada conexión arranca sin datos: `onopen` solo dice que el server aceptó
    // la conexión, no que vaya a mandar algo.
    receivedData = false;

    es.onopen = () => {
      // (Re)conectado: reconciliar con Mongo (fuente de verdad). El pub/sub es
      // volátil y sin replay, así que cualquier evento emitido durante el corte
      // se recupera acá. Se omite la primera apertura (el montaje ya cargó).
      if (openedOnce) handlers.onReconnect?.();
      openedOnce = true;
    };

    const route = (event) => {
      // Primer evento REAL: recién acá la conexión está sana y la racha de
      // fallos queda atrás. Reiniciar en `onopen` haría que un 200 que cierra
      // sin mandar nada devolviera el delay al mínimo.
      if (!receivedData) {
        receivedData = true;
        reconnectDelay = SSE_RECONNECT_DELAY;
      }
      try {
        const data = JSON.parse(event.data);
        if (event.type === "new_conversation") handlers.onNewConversation?.(data);
        else if (event.type === "new_message") handlers.onNewMessage?.(data);
        else if (event.type === "assignment_change") handlers.onAssignmentChange?.(data);
        else if (event.type === "status_change") handlers.onStatusChange?.(data);
        else if (event.type === "message_status") handlers.onMessageStatus?.(data);
        else if (event.type === "notifications_inbound") handlers.onNotificationsInbound?.(data);
      } catch { /* ignore parse errors */ }
    };

    es.addEventListener("new_conversation", route);
    es.addEventListener("new_message", route);
    es.addEventListener("assignment_change", route);
    es.addEventListener("status_change", route);
    // Sin este addEventListener el evento se pierde: EventSource solo entrega a
    // onmessage los bloques SIN `event:`, los tipados hay que suscribirlos.
    es.addEventListener("message_status", route);
    // Respuestas en números de solo notificaciones: tipo propio para que los
    // bundles que no lo escuchan (y el inbox sin "ver todas") no las muestren.
    es.addEventListener("notifications_inbound", route);

    es.onerror = () => {
      if (stopped) return;
      handlers.onError?.(new Error("Omni SSE error"));
      try { es.close(); } catch {}
      scheduleReconnect();
    };
  }

  open();

  return function cleanup() {
    stopped = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    try { es?.close(); } catch {}
  };
}
